import { PrimaryAnnotatedType, SecondaryAnnotatedType } from '@/components/annotated-type';
import type { BarChartItem } from '@/components/bar-chart/vertical';
import { StandaloneVerticalBarChart } from '@/components/bar-chart/vertical';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { CenteredStoryText, StoryText } from '@/components/microstory/text';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { t, Trans } from '@lingui/macro';
import { useState } from 'react';

type MentalBurdensChapter3Props = {
  data: MentalBurdensData;
};

export function MentalBurdensChapter3(props: MentalBurdensChapter3Props) {
  const { data } = props;

  const formatPercentage = useNumberFormatting('percent');

  const [chart1Data, setChart1Data] = useState<BarChartItem[]>();
  const [chart1Visible, setChart1Visible] = useState(false);
  const handleStep1Enter = (e: OnStepEnterExitArgs<BarChartItem[]>) => {
    setChart1Visible(true);
    if (e.data) {
      setChart1Data(e.data);
    }
  };

  const [chart2Data, setChart2Data] = useState<BarChartItem[]>();
  const [chart2Visible, setChart2Visible] = useState(false);
  const handleStep2Enter = (e: OnStepEnterExitArgs<BarChartItem[]>) => {
    setChart2Visible(true);
    if (e.data) {
      setChart2Data(e.data);
    }
  };

  const [chart3Data, setChart3Data] = useState<BarChartItem[]>();
  const [chart3Visible, setChart3Visible] = useState(false);
  const handleStep3Enter = (e: OnStepEnterExitArgs<BarChartItem[]>) => {
    setChart3Visible(true);
    if (e.data) {
      setChart3Data(e.data);
    }
  };

  return (
    <>
      <ScrollRoot>
        <ScrollGraphic>
          <StandaloneVerticalBarChart
            label={t({
              id: 'mental-burdens.chapter-3.chart-1.label',
              message: '% of young people who often feel anxious, nervous or worried',
            })}
            data={chart1Data}
            visible={chart1Visible}
            formatValue={formatPercentage}
          />
        </ScrollGraphic>

        <ScrollSteps onStepEnter={handleStep1Enter} offset={0.7} hasGraphic>
          <ScrollStep
            data={[
              {
                value: data.often_worried_age['15-19'],
                label: t({
                  message: '15–19 year-olds',
                  id: 'mental-burdens.chapter-3.chart-1.label-1',
                }),
              },
              {
                value: data.often_worried_age['20-24'],
                label: t({
                  message: '20-24 year-olds',
                  id: 'mental-burdens.chapter-3.chart-1.label-2',
                }),
              },
            ]}
          >
            <StoryText>
              <Trans id="mental-burdens.chapter-3.text-1">
                <SecondaryAnnotatedType>Young people aged 20-24</SecondaryAnnotatedType> are more
                likely to report often feeling anxious, nervous or worried than{' '}
                <PrimaryAnnotatedType>those aged 15-19.</PrimaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>

      <ScrollRoot>
        <ScrollGraphic>
          <StandaloneVerticalBarChart
            label={t({
              id: 'mental-burdens.chapter-3.chart-2.label',
              message: '% of young people who often feel anxious, nervous or worried',
            })}
            data={chart2Data}
            visible={chart2Visible}
            formatValue={formatPercentage}
          />
        </ScrollGraphic>

        <ScrollSteps onStepEnter={handleStep2Enter} offset={0.7} hasGraphic>
          <ScrollStep
            data={[
              {
                value: data.often_worried_college.completed,
                label: t({
                  message: 'Completed college',
                  id: 'mental-burdens.chapter-3.chart-2.label-1',
                }),
              },
              {
                value: data.often_worried_college.not_completed,
                label: t({
                  message: 'Not completed college',
                  id: 'mental-burdens.chapter-3.chart-2.label-2',
                }),
              },
            ]}
          >
            <StoryText>
              <Trans id="mental-burdens.chapter-3.text-3">
                <PrimaryAnnotatedType>Young people who have completed college</PrimaryAnnotatedType>{' '}
                are less likely to report feeling anxious, nervous or worried than{' '}
                <SecondaryAnnotatedType>those who haven't.</SecondaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>

      <ScrollRoot>
        <ScrollGraphic>
          <StandaloneVerticalBarChart
            label={t({
              id: 'mental-burdens.chapter-3.chart-3.label',
              message: '% of young people who often feel anxious, nervous or worried',
            })}
            data={chart3Data}
            visible={chart3Visible}
            formatValue={formatPercentage}
          />
        </ScrollGraphic>
        <ScrollSteps onStepEnter={handleStep3Enter} offset={0.7} hasGraphic>
          <ScrollStep
            data={[
              {
                value: data.often_worried_income.uncomfortable,
                label: t({
                  message: 'Finding it difficult to get by',
                  id: 'mental-burdens.chapter-3.chart-3.label-1',
                }),
              },
              {
                value: data.often_worried_income.not_uncomfortable,
                label: t({
                  message: 'Living comfortably',
                  id: 'mental-burdens.chapter-3.chart-3.label-2',
                }),
              },
            ]}
          >
            <StoryText>
              <Trans id="mental-burdens.chapter-3.text-2">
                <PrimaryAnnotatedType>
                  Those who say they find it difficult to get by financially
                </PrimaryAnnotatedType>{' '}
                are more likely to report often feeling anxious, nervous or worried than{' '}
                <SecondaryAnnotatedType>those who say they're comfortable.</SecondaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>

      <CenteredStoryText>
        <Trans id="mental-burdens.chapter-3.text-4">
          There is growing concern about the state of mental health among young people around the
          world. That concern is clearly justified.
        </Trans>
      </CenteredStoryText>
    </>
  );
}
